import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteName, siteUrl } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [activeCategory, setActiveCategory] = useState(currentCategory.slug);

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteName}`,
    description: currentCategory.description,
    url: `${siteUrl}/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteName,
      url: siteUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `${siteUrl}/category/${activeCategory}` },
    ],
  };

  return (
    <>
      <SEO
        title={`${currentCategory.name} — ${siteName}`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},中文字幕 日韩,日韩影视字幕`}
        canonicalUrl={`${siteUrl}/category/${activeCategory}`}
        schema={{ ...categorySchema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="px-4 md:px-6 pb-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base">{currentCategory.description}</p>
          </div>

          <div className="px-4 md:px-6 mb-8">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1.5 text-xs opacity-70">({cat.articleCount})</span>
                </button>
              ))}
            </div>
          </div>

          <div className="px-4 md:px-6 pb-14">
            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </span>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col rounded-2xl bg-background-100 hover:bg-background-100 transition-all duration-300 overflow-hidden cursor-pointer"
                  >
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={`${article.title} - 中文字幕 日韩`}
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="text-xs text-foreground-500">{article.publishDate}</span>
                        <span className="text-xs text-foreground-500 flex items-center gap-1">
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </span>
                          {article.views.toLocaleString()}
                        </span>
                      </div>
                      <h2 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 line-clamp-2 flex-1">{article.excerpt}</p>
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-md bg-background-200 text-foreground-600 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}